import { useEffect, useState, useCallback, useMemo } from "react";
import Rack from "./Rack";
import httpService from "../services/http/stores/outlet.service";
import shelfHttpService from "../services/http/shelf-management/shelf.service";
import { useNavigate, useParams } from "react-router-dom";
import notificationService from "../services/toaster.service";
import UnlimitLoading from "../components/ui-kit/unlimit-loading";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Download, X } from "feather-icons-react/build/IconComponents";

const RackList = () => {
  const location = useLocation();
  const { name, image } = location.state || {};
  const [racks, setRacks] = useState([]);
  const [originalRacks, setOriginalRacks] = useState([]); // Store original data
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cctvList, setCCTVList] = useState([]);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  // Track individual rack save states
  const [rackSaveStates, setRackSaveStates] = useState(new Map());

  const { id: outletId } = useParams();
  const { t } = useTranslation();

  // Memoized outlet display info
  const outletDisplayInfo = useMemo(() => {
    if (name && image) {
      return { name, image };
    }
    return null;
  }, [name, image]);

  // Filter racks based on search term
  const filteredRacks = useMemo(() => {
    if (!searchTerm.trim()) {
      return originalRacks;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return (
      originalRacks
        .map((rack) => ({
          ...rack,
          shelves: (rack.shelves || []).map((shelf) => ({
            ...shelf,
            sensors: (shelf.sensors || []).map((sensor) => ({
              ...sensor,
              products: (sensor.products || []).filter(
                (product) =>
                  String(product.lm_product_number)
                    ?.toLowerCase()
                    .includes(searchLower) ||
                  String(product.ean)?.toLowerCase().includes(searchLower)
              ),
            })),
          })),
        }))
        // Only include racks that have at least one matching product
        .filter((rack) =>
          rack.shelves?.some((shelf) =>
            shelf.sensors?.some((sensor) =>
              sensor.products?.some(
                (product) =>
                  String(product.lm_product_number)
                    ?.toLowerCase()
                    .includes(searchLower) ||
                  String(product.ean)?.toLowerCase().includes(searchLower)
              )
            )
          )
        )
    );
  }, [originalRacks, searchTerm]);

  useEffect(() => {
    setRacks(filteredRacks);
  }, [filteredRacks]);

  const areAllRacksSaved = useMemo(() => {
    if (racks.length === 0) return true;

    return racks.every((rack) => {
      const saveState = rackSaveStates.get(rack.id);
      return saveState === true; // Only true means saved, undefined/false means unsaved
    });
  }, [racks, rackSaveStates]);

  // Update hasUnsavedChanges based on individual rack states
  useEffect(() => {
    setHasUnsavedChanges(!areAllRacksSaved);
  }, [areAllRacksSaved]);

  const onClickExport = async () => {
    if (!outletId) {
      notificationService.errorMessage("Invalid outlet ID");
      return;
    }

    try {
      setIsExporting(true);

      const response = await shelfHttpService.exportShelfManagement(outletId);

      console.log("Response from export:", response);
      if (response?.status === 200 && response.data?.data?.download_url) {
        const downloadUrl = response.data.data.download_url;
        const filename =
          response.data.data.filename ||
          `shelf_management_outlet_${outletId}.xlsx`;

        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        link.style.display = "none"; // Hide the link
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        notificationService.successMessage("File downloaded successfully!");
      } else {
        throw new Error("Download URL not found in response");
      }
    } catch (error) {
      console.error("Error exporting shelf management data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to export data";
      notificationService.errorMessage(errorMessage);
    } finally {
      setIsExporting(false); // if you have loading state
    }
  };

  const fetchOutletData = useCallback(async () => {
    if (!outletId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await shelfHttpService.getSaveProduct(outletId);

      if (response?.status === 200 && response.data?.data) {
        const processedRacks = response.data.data.map((rack) => ({
          ...rack,
          shelves: (rack.shelves || []).map((shelf) => ({
            ...shelf,
            sensors: (shelf.sensors || []).map((sensor) => ({
              ...sensor,
              products: sensor.products || [],
            })),
          })),
        }));

        setOriginalRacks(processedRacks);

        // Initialize all racks as saved when loaded from server
        const newSaveStates = new Map();
        processedRacks.forEach((rack) => {
          newSaveStates.set(rack.id, true);
        });
        setRackSaveStates(newSaveStates);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("Error fetching outlet data:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch outlet data";
      setError(`${errorMessage}. Please try again later.`);
      notificationService.errorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  // Fetch CCTV data
  const fetchCCTVData = useCallback(async () => {
    if (!outletId) return;

    try {
      const response = await httpService.shopCameraList(outletId);

      if (response?.status === 200 && response.data?.data?.data) {
        setCCTVList(response.data.data.data);
      }
    } catch (error) {
      console.error("Error fetching CCTV data:", error);
      // Don't show error notification for CCTV as it's not critical
    }
  }, [outletId]);

  // Initial data loading
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchOutletData(), fetchCCTVData()]);
    };

    loadData();
  }, [fetchOutletData, fetchCCTVData]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Add rack with smart naming to avoid duplicates
  const addRack = useCallback(() => {
    // Find the next available rack number from original racks
    const existingRackNumbers = originalRacks
      .map((rack) => {
        const match = rack.name?.match(/^Rack (\d+)$/);
        return match ? parseInt(match[1]) : null;
      })
      .filter((num) => num !== null);

    let nextRackNumber = 1;
    while (existingRackNumbers.includes(nextRackNumber)) {
      nextRackNumber++;
    }

    const newRackId = `temp_rack_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const newRack = {
      id: newRackId,
      name: `Rack ${nextRackNumber}`,
      shelves: [],
      cameras: [],
    };

    setOriginalRacks((prevRacks) => [...prevRacks, newRack]);

    // Mark new rack as unsaved
    setRackSaveStates((prev) => new Map(prev).set(newRackId, false));

    console.log("Added new rack:", newRack);
  }, [originalRacks]);

  // Delete rack
  const deleteRack = useCallback((rackId) => {
    setOriginalRacks((prevRacks) =>
      prevRacks.filter((rack) => rack.id !== rackId)
    );

    // Remove from save states
    setRackSaveStates((prev) => {
      const newMap = new Map(prev);
      newMap.delete(rackId);
      return newMap;
    });
  }, []);

  // Handle individual rack changes
  const handleRackChange = useCallback((rackId) => {
    // Mark this specific rack as unsaved
    setRackSaveStates((prev) => new Map(prev).set(rackId, false));
  }, []);

  // Handle individual rack save success
  const handleRackSaveSuccess = useCallback((rackId) => {
    // Mark this specific rack as saved
    setRackSaveStates((prev) => new Map(prev).set(rackId, true));
  }, []);

  // Utility functions for data cleaning
  const cleanTempId = useCallback((id, tempPrefix) => {
    if (!id) return null;
    return id.toString().includes(tempPrefix) ? null : id;
  }, []);

  const normalizeCameras = useCallback((cameras) => {
    if (!Array.isArray(cameras)) return [];
    return cameras
      .map((camera) =>
        typeof camera === "object" && camera?.id ? camera.id : camera
      )
      .filter(Boolean);
  }, []);

  // Validate data before saving
  const validateRacksData = useCallback((racksData) => {
    const errors = [];

    racksData.forEach((rack, rackIndex) => {
      if (!rack.name?.trim()) {
        errors.push(`Rack ${rackIndex + 1} must have a name`);
      }

      rack.shelves?.forEach((shelf, shelfIndex) => {
        if (!shelf.name?.trim()) {
          errors.push(
            `Shelf ${shelfIndex + 1} in Rack ${rackIndex + 1} must have a name`
          );
        }

        shelf.sensors?.forEach((sensor, sensorIndex) => {
          if (!sensor.sensor_number) {
            errors.push(
              `Sensor ${sensorIndex + 1} in Shelf ${shelfIndex + 1}, Rack ${
                rackIndex + 1
              } must have a sensor number`
            );
          }
        });
      });
    });

    return errors;
  }, []);

  // Save all racks with improved error handling and validation
  const saveAllRacks = useCallback(async () => {
    if (!outletId) {
      notificationService.errorMessage("Invalid outlet ID");
      return;
    }

    // Use original racks for saving (not filtered ones)
    const validationErrors = validateRacksData(originalRacks);
    if (validationErrors.length > 0) {
      notificationService.errorMessage(
        `Validation failed: ${validationErrors[0]}`
      );
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      outlet: {
        id: outletId,
        racks: originalRacks.map((rack) => ({
          id: cleanTempId(rack.id, "temp_rack_"),
          name: rack.name?.trim() || "",
          // Add processor fields to payload
          p_number: rack.p_number?.trim() || null,
          p_ip: rack.p_ip?.trim() || null,
          p_port: rack.p_port ? parseInt(rack.p_port) : null,
          p_min_weight_change: rack.p_min_weight_change
            ? parseFloat(rack.p_min_weight_change)
            : null,
          cameras: normalizeCameras(rack.cameras),
          shelves: (rack.shelves || []).map((shelf) => ({
            id: cleanTempId(shelf.id, "temp_shelf_"),
            name: shelf.name?.trim() || "",
            cameras_location: shelf.cameras_location || {},
            sensors: (shelf.sensors || []).map((sensor) => ({
              id: cleanTempId(sensor.id, "temp_sensor_"),
              sensor_number: sensor.sensor_number,
              products: (sensor.products || []).map((product) => ({
                product_id: product.id,
                quantity: Math.max(0, parseInt(product.quantity) || 0),
              })),
            })),
          })),
        })),
      },
    };

    console.log("Saving payload:", JSON.stringify(payload, null, 2));

    try {
      const response = await shelfHttpService.saveShelfRecord(payload, true);

      if (response?.status === 200) {
        notificationService.successMessage(
          t("shelf.dataSavedSuccessfully") || "Data saved successfully!"
        );

        // Mark all racks as saved
        const newSaveStates = new Map();
        originalRacks.forEach((rack) => {
          newSaveStates.set(rack.id, true);
        });
        setRackSaveStates(newSaveStates);

        // Refresh data to get updated IDs from server
        await fetchOutletData();
      } else {
        throw new Error(`Server returned status ${response?.status}`);
      }
    } catch (err) {
      console.error("Error saving racks data:", err);
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to save racks data";
      setError(`${errorMessage}. Please try again.`);
      notificationService.errorMessage(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [
    outletId,
    originalRacks,
    cleanTempId,
    normalizeCameras,
    validateRacksData,
    fetchOutletData,
    t,
  ]);

  const getRackNumber = (rackName) => {
    const match = rackName?.match(/^Rack (\d+)$/);
    return match ? match[1] : "";
  };

  // Handle before unload to warn about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Memoized rack count for performance
  const rackCount = useMemo(() => racks.length, [racks.length]);
  const originalRackCount = useMemo(
    () => originalRacks.length,
    [originalRacks.length]
  );

  // Track changes in racks (for general changes, not individual rack changes)
  const handleRacksChange = useCallback(() => {
    // This is for general changes that affect all racks
    // Mark all racks as potentially unsaved
    const newSaveStates = new Map();
    originalRacks.forEach((rack) => {
      newSaveStates.set(rack.id, false);
    });
    setRackSaveStates(newSaveStates);
  }, [originalRacks]);

  // Count unsaved racks for display
  const unsavedRacksCount = useMemo(() => {
    return originalRacks.filter((rack) => rackSaveStates.get(rack.id) !== true)
      .length;
  }, [originalRacks, rackSaveStates]);

  // Calculate total products matching search
  const matchingProductsCount = useMemo(() => {
    if (!searchTerm.trim()) return 0;

    return racks.reduce(
      (total, rack) =>
        total +
          rack.shelves?.reduce(
            (shelfTotal, shelf) =>
              shelfTotal +
                shelf.sensors?.reduce(
                  (sensorTotal, sensor) =>
                    sensorTotal + (sensor.products?.length || 0),
                  0
                ) || 0,
            0
          ) || 0,
      0
    );
  }, [racks, searchTerm]);

  // Loading state
  if (loading && originalRacks.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="d-flex flex-column align-items-center">
          <UnlimitLoading type="spin" color="#9A0000" height={60} width={60} />
          <div className="mt-2 text-muted">Loading shelf data...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && originalRacks.length === 0) {
    return (
      <div className="alert alert-danger" role="alert">
        <h6 className="alert-heading">Error Loading Data</h6>
        <p className="mb-2">{error}</p>
        <button
          className="btn btn-outline-danger btn-sm"
          onClick={() => {
            setError(null);
            fetchOutletData();
          }}
        >
          {t("general.button.retry") || "Retry"}
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center">
        <>
          {outletDisplayInfo ? (
            <div className="d-flex align-items-center mb-2 p-2 rounded">
              <img
                src={outletDisplayInfo.image}
                alt={outletDisplayInfo.name}
                className="me-3 rounded"
                style={{ width: "80px", height: "80px", objectFit: "cover" }}
              />
              <div>
                <h5 className="mb-1 fw-bold">{outletDisplayInfo.name}</h5>
                <small className="text-muted">
                  {originalRackCount}{" "}
                  {originalRackCount === 1 ? "rack" : "racks"} configured
                  {unsavedRacksCount > 0 && (
                    <span className="text-warning ms-2">
                      ({unsavedRacksCount} unsaved)
                    </span>
                  )}
                </small>
              </div>
            </div>
          ) : (
            <div className="alert alert-info" role="alert">
              <small>No outlet information available.</small>
            </div>
          )}
        </>
        <div>
          <button className="btn btn-primary" onClick={() => onClickExport()}>
            {isExporting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Exporting...
              </>
            ) : (
              <>
                <Download size={16} className="me-1" />
                Export as Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-3">
        <div className="row d-flex justify-content-end align-items-center">
          <div className="col-md-3">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search LM Product No or EAN"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Search Results Info */}
        {searchTerm.trim() && (
          <div className="mt-2">
            <small className="text-muted">
              {matchingProductsCount > 0 ? (
                <>
                  Found {matchingProductsCount} product
                  {matchingProductsCount !== 1 ? "s" : ""} in {rackCount} rack
                  {rackCount !== 1 ? "s" : ""} matching &apos;{searchTerm}&apos;
                </>
              ) : (
                <>No products found matching &apos;{searchTerm}&apos;</>
              )}
            </small>
          </div>
        )}
      </div>

      {/* Unsaved changes warning */}
      {hasUnsavedChanges && (
        <div className="alert alert-warning" role="alert">
          <small>
            <strong>Unsaved changes:</strong> You have {unsavedRacksCount}{" "}
            unsaved rack{unsavedRacksCount !== 1 ? "s" : ""}. Don&apos;t forget
            to save your work.
          </small>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Racks List */}
      <div className="row">
        {racks.map((rack, index) => (
          <div key={rack.id || index} className="col-12 mb-3">
            <Rack
              id={rack.id}
              number={index + 1}
              name={rack.name}
              shelves={rack.shelves}
              cameras={rack.cameras}
              // Pass processor fields
              p_number={rack.p_number}
              p_ip={rack.p_ip}
              p_port={rack.p_port}
              p_min_weight_change={rack.p_min_weight_change}
              setRacks={setOriginalRacks} // Update original racks, not filtered
              outlet_id={outletId}
              cctvList={cctvList}
              onDelete={() => deleteRack(rack.id)}
              onChange={() => handleRackChange(rack.id)}
              onSaveSuccess={() => handleRackSaveSuccess(rack.id)}
              // Pass save state for this rack
              hasUnsavedChanges={rackSaveStates.get(rack.id) !== true}
            />
          </div>
        ))}
      </div>

      {/* Empty state */}
      {racks.length === 0 && !loading && (
        <div className="text-center py-5">
          <div className="text-muted mb-3">
            {searchTerm.trim() ? (
              <>
                <h6>No matching products found</h6>
                <p>
                  No products with product number containing &#39;{searchTerm}
                  &#39; were found.
                </p>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={clearSearch}
                >
                  Clear search to see all racks
                </button>
              </>
            ) : (
              <>
                <h6>No racks configured</h6>
                <p>Start by adding your first rack to organize products.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex gap-3 mt-4 pb-4">
        <button
          onClick={addRack}
          className="btn btn-primary"
          disabled={loading || saving}
          style={{
            borderColor: "#333",
            borderRadius: "4px",
            fontSize: "12px",
            height: "35px",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <i className="fas fa-plus me-2"></i>
          {t("shelf.addRack") || "Add Rack"}
        </button>

        <button
          onClick={saveAllRacks}
          className="btn btn-success"
          disabled={loading || saving || originalRacks.length === 0}
          style={{
            borderColor: "#333",
            borderRadius: "4px",
            fontSize: "12px",
            height: "35px",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          {saving ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save me-2"></i>
              {t("shelf.saveAll") || "Save All"}
              {hasUnsavedChanges && ` (${unsavedRacksCount})`}
            </>
          )}
        </button>

        {/* Refresh button */}
        <button
          onClick={fetchOutletData}
          className="btn btn-outline-secondary"
          disabled={loading || saving}
          title="Refresh data"
          style={{
            borderColor: "#333",
            borderRadius: "4px",
            fontSize: "12px",
            height: "35px",
            padding: "0 12px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
        </button>
      </div>

      {/* Statistics */}
      {originalRacks.length > 0 && (
        <div className="mt-2 p-3 rounded" style={{ background: "#f1f1f1ff" }}>
          <small className="text-muted">
            <strong>Statistics:</strong> {originalRackCount} total racks,{" "}
            {originalRacks.reduce(
              (total, rack) => total + (rack.shelves?.length || 0),
              0
            )}{" "}
            shelves,{" "}
            {originalRacks.reduce(
              (total, rack) =>
                total +
                (rack.shelves?.reduce(
                  (shelfTotal, shelf) =>
                    shelfTotal + (shelf.sensors?.length || 0),
                  0
                ) || 0),
              0
            )}{" "}
            sensors
            {searchTerm.trim() && (
              <>
                {" | "}
                <strong>Filtered:</strong> {rackCount} racks shown,{" "}
                {matchingProductsCount} products matching search
              </>
            )}
            {hasUnsavedChanges && (
              <span className="text-warning ms-3">
                <strong>•</strong> {unsavedRacksCount} rack
                {unsavedRacksCount !== 1 ? "s" : ""} with unsaved changes
              </span>
            )}
          </small>
        </div>
      )}
    </div>
  );
};

export default RackList;
